import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { reportAPI, departmentAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, Clock, DollarSign, TrendingUp, FileSpreadsheet } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

// Color helper functions to avoid nested ternaries
const getIconBgColor = (color) => {
  if (color?.includes('emerald')) return 'bg-emerald-100';
  if (color?.includes('blue')) return 'bg-blue-100';
  if (color?.includes('amber')) return 'bg-amber-100';
  return 'bg-slate-100';
};

const getIconTextColor = (color) => {
  if (color?.includes('emerald')) return 'text-emerald-600';
  if (color?.includes('blue')) return 'text-blue-600';
  if (color?.includes('amber')) return 'text-amber-600';
  return 'text-slate-600';
};

// Moved outside to avoid re-creation on each render
const StatCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
  <div className="dashboard-card">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{title}</p>
        <p className={`text-2xl font-heading font-semibold ${color || 'text-slate-900'}`}>{value}</p>
        {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
      </div>
      <div className={`p-3 rounded-lg ${getIconBgColor(color)}`}>
        <Icon className={`w-5 h-5 ${getIconTextColor(color)}`} />
      </div>
    </div>
    {trend && (
      <div className="mt-3 flex items-center text-sm">
        <TrendingUp className={`w-4 h-4 mr-1 ${trend > 0 ? 'text-emerald-500' : 'text-red-500'}`} />
        <span className={trend > 0 ? 'text-emerald-600' : 'text-red-600'}>{trend > 0 ? '+' : ''}{trend}%</span>
        <span className="text-slate-400 ml-2">vs last month</span>
      </div>
    )}
  </div>
);

const Analytics = () => {
  const [hrAnalytics, setHRAnalytics] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [claimsAnalytics, setClaimsAnalytics] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedDept, setSelectedDept] = useState('');

  useEffect(() => {
    fetchHRAnalytics();
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedDept]);

  const fetchHRAnalytics = async () => {
    try {
      const res = await reportAPI.getHRAnalytics();
      setHRAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to fetch HR analytics');
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await departmentAPI.getAll();
      setDepartments(res.data);
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [attendanceRes, payrollRes, claimsRes] = await Promise.all([
        reportAPI.getAttendanceSummary(selectedMonth, selectedDept || undefined),
        reportAPI.getPayrollSummary(selectedMonth),
        reportAPI.getClaimsAnalytics()
      ]);
      setAttendanceReport(attendanceRes.data);
      setPayrollSummary(payrollRes.data);
      setClaimsAnalytics(claimsRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !hrAnalytics) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="analytics-page">
      <Header 
        title="Analytics & Reports" 
        subtitle="Comprehensive HR analytics and reporting dashboard"
      />
      
      <div className="p-8">
        {/* Overview Stats */}
        {hrAnalytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard 
              title="Total Employees" 
              value={hrAnalytics.total_employees || hrAnalytics.employees?.total || 0}
              subValue={`${hrAnalytics.by_department?.length || hrAnalytics.departments?.total || 0} departments`}
              icon={Users}
              color="text-blue-600"
            />
            <StatCard 
              title="Monthly Payroll" 
              value={formatCurrency(hrAnalytics.payroll?.total_gross || 0)}
              subValue={`${hrAnalytics.payroll?.employees_processed || 0} processed`}
              icon={DollarSign}
              color="text-emerald-600"
            />
            <StatCard 
              title="Pending Leaves" 
              value={hrAnalytics.pending_leaves || hrAnalytics.leaves?.pending || 0}
              subValue={`${hrAnalytics.leaves?.approved || 0} approved`}
              icon={Clock}
              color="text-amber-600"
            />
            <StatCard 
              title="Pending Claims" 
              value={hrAnalytics.pending_claims || hrAnalytics.claims?.pending_count || 0}
              subValue={formatCurrency(hrAnalytics.claims?.approved_amount || 0) + ' approved'}
              icon={FileSpreadsheet}
              color="text-purple-600"
            />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-40"
            data-testid="month-filter"
          />
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-48" data-testid="department-filter">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="attendance" className="space-y-6">
          <TabsList>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="payroll">Payroll</TabsTrigger>
            <TabsTrigger value="claims">Claims</TabsTrigger>
            <TabsTrigger value="workforce">Workforce</TabsTrigger>
          </TabsList>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            {attendanceReport && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{attendanceReport.summary?.total_present_days || 0}</p>
                    <p className="text-sm text-slate-500">Total Present Days</p>
                  </div>
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-red-500">{attendanceReport.summary?.total_absent_days || 0}</p>
                    <p className="text-sm text-slate-500">Total Absent Days</p>
                  </div>
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{attendanceReport.summary?.total_overtime_hours || 0}h</p>
                    <p className="text-sm text-slate-500">Total Overtime</p>
                  </div>
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{attendanceReport.summary?.average_attendance_percentage || 0}%</p>
                    <p className="text-sm text-slate-500">Avg Attendance</p>
                  </div>
                </div>

                {/* Employee-wise Breakdown */}
                <div className="dashboard-card" data-testid="attendance-table">
                  <h3 className="font-heading text-lg font-semibold mb-4">Employee Attendance - {selectedMonth}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Present</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">WFH</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Absent</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Leave</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Hours</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">OT</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceReport.records?.map((record) => (
                          <tr key={record.employee_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <p className="font-medium">{record.employee_name}</p>
                              <p className="text-xs text-slate-400">{record.employee_code}</p>
                            </td>
                            <td className="py-3 px-4 text-center text-emerald-600 font-medium">{record.present_days}</td>
                            <td className="py-3 px-4 text-center text-purple-600">{record.wfh_days}</td>
                            <td className="py-3 px-4 text-center text-red-500">{record.absent_days}</td>
                            <td className="py-3 px-4 text-center text-blue-500">{record.leave_days}</td>
                            <td className="py-3 px-4 text-center">{record.total_working_hours}h</td>
                            <td className="py-3 px-4 text-center text-amber-600">{record.overtime_hours}h</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-semibold ${record.attendance_percentage >= 90 ? 'text-emerald-600' : record.attendance_percentage >= 75 ? 'text-amber-600' : 'text-red-500'}`}>
                                {record.attendance_percentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(!attendanceReport.records || attendanceReport.records.length === 0) && (
                      <p className="text-center py-8 text-slate-400">No attendance data for selected period</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Payroll Tab */}
          <TabsContent value="payroll">
            {payrollSummary && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="dashboard-card p-4 text-center bg-emerald-50">
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(payrollSummary.total_gross_salary || 0)}</p>
                    <p className="text-sm text-emerald-600">Gross Salary</p>
                  </div>
                  <div className="dashboard-card p-4 text-center bg-red-50">
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(
                      (payrollSummary.statutory_deductions?.pf || 0) +
                      (payrollSummary.statutory_deductions?.esi || 0) +
                      (payrollSummary.statutory_deductions?.tds || 0) +
                      (payrollSummary.statutory_deductions?.professional_tax || 0)
                    )}</p>
                    <p className="text-sm text-red-600">Total Deductions</p>
                  </div>
                  <div className="dashboard-card p-4 text-center bg-blue-50">
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(payrollSummary.total_net_salary || 0)}</p>
                    <p className="text-sm text-blue-600">Net Salary</p>
                  </div>
                  <div className="dashboard-card p-4 text-center bg-slate-50">
                    <p className="text-2xl font-bold text-slate-700">{payrollSummary.total_employees || 0}</p>
                    <p className="text-sm text-slate-600">Employees</p>
                  </div>
                </div>

                {/* Statutory Breakdown */}
                <div className="dashboard-card" data-testid="statutory-breakdown">
                  <h3 className="font-heading text-lg font-semibold mb-4">Statutory Deductions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Provident Fund</p>
                      <p className="text-xl font-bold">{formatCurrency(payrollSummary.statutory_deductions?.pf || 0)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">ESI</p>
                      <p className="text-xl font-bold">{formatCurrency(payrollSummary.statutory_deductions?.esi || 0)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">TDS</p>
                      <p className="text-xl font-bold">{formatCurrency(payrollSummary.statutory_deductions?.tds || 0)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Professional Tax</p>
                      <p className="text-xl font-bold">{formatCurrency(payrollSummary.statutory_deductions?.professional_tax || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* By Department */}
                <div className="dashboard-card" data-testid="dept-payroll">
                  <h3 className="font-heading text-lg font-semibold mb-4">Payroll by Department</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Department</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Employees</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Gross</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Deductions</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(payrollSummary.by_department || {}).map(([dept, data]) => (
                          <tr key={dept} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{dept}</td>
                            <td className="py-3 px-4 text-right">{data.count}</td>
                            <td className="py-3 px-4 text-right">{formatCurrency(data.gross)}</td>
                            <td className="py-3 px-4 text-right text-red-500">{formatCurrency(data.deductions)}</td>
                            <td className="py-3 px-4 text-right font-semibold">{formatCurrency(data.net)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* By Status */}
                <div className="dashboard-card" data-testid="status-payroll">
                  <h3 className="font-heading text-lg font-semibold mb-4">Payroll by Status</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(payrollSummary.by_status || {}).map(([status, data]) => (
                      <div key={status} className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500 uppercase">{status.replace('_', ' ')}</p>
                        <p className="text-lg font-bold">{data.count}</p>
                        <p className="text-sm text-slate-600">{formatCurrency(data.net)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Claims Tab */}
          <TabsContent value="claims">
            {claimsAnalytics && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-slate-900">{claimsAnalytics.total_claims || 0}</p>
                    <p className="text-sm text-slate-500">Total Claims</p>
                  </div>
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{formatCurrency(claimsAnalytics.total_amount || 0)}</p>
                    <p className="text-sm text-slate-500">Total Amount</p>
                  </div>
                  <div className="dashboard-card p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">
                      {formatCurrency((claimsAnalytics.by_status?.approved?.total || 0) + (claimsAnalytics.by_status?.paid?.total || 0))}
                    </p>
                    <p className="text-sm text-slate-500">Approved/Paid</p>
                  </div>
                </div>

                {/* By Category */}
                <div className="dashboard-card" data-testid="claims-by-category">
                  <h3 className="font-heading text-lg font-semibold mb-4">Claims by Category</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Category</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Count</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Total</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Approved</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Pending</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Rejected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(claimsAnalytics.by_category || {}).map(([category, data]) => (
                          <tr key={category} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium capitalize">{category}</td>
                            <td className="py-3 px-4 text-right">{data.count}</td>
                            <td className="py-3 px-4 text-right font-semibold">{formatCurrency(data.total)}</td>
                            <td className="py-3 px-4 text-right text-emerald-600">{formatCurrency(data.approved)}</td>
                            <td className="py-3 px-4 text-right text-amber-600">{formatCurrency(data.pending)}</td>
                            <td className="py-3 px-4 text-right text-red-500">{formatCurrency(data.rejected)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Top Claimants */}
                <div className="dashboard-card" data-testid="top-claimants">
                  <h3 className="font-heading text-lg font-semibold mb-4">Top Claimants</h3>
                  <div className="space-y-3">
                    {claimsAnalytics.top_claimants?.map((claimant, index) => (
                      <div key={claimant.employee_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <span className="font-medium">{claimant.name}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(claimant.total)}</p>
                          <p className="text-xs text-slate-400">{claimant.count} claims</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Workforce Tab */}
          <TabsContent value="workforce">
            {hrAnalytics && (
              <div className="space-y-6">
                {/* Employee Types */}
                <div className="dashboard-card" data-testid="employee-types">
                  <h3 className="font-heading text-lg font-semibold mb-4">Employment Types</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(hrAnalytics.employees?.by_type || {}).map(([type, count]) => (
                      <div key={type} className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-sm text-slate-500 capitalize">{type}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Department Distribution */}
                <div className="dashboard-card" data-testid="dept-distribution">
                  <h3 className="font-heading text-lg font-semibold mb-4">Department Distribution</h3>
                  <div className="space-y-3">
                    {hrAnalytics.employees?.by_department?.map((dept, index) => (
                      <div key={dept.department} className="flex items-center gap-4">
                        <div className="w-32 text-sm font-medium">{dept.department}</div>
                        <div className="flex-1 bg-slate-100 rounded-full h-8 overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 flex items-center justify-end pr-3"
                            style={{ width: `${(dept.count / hrAnalytics.employees.total) * 100}%` }}
                          >
                            <span className="text-xs font-medium text-white">{dept.count}</span>
                          </div>
                        </div>
                        <div className="w-12 text-sm text-right text-slate-500">
                          {Math.round((dept.count / hrAnalytics.employees.total) * 100)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Analytics;
